// FacilityCard — one facility's evolving illustration with status
// pills and a direct deep-link to its management tab (anti-FM26:
// data and actions never buried behind modals).

import { useNavigate } from 'react-router-dom'
import {
  FacilityVisual,
  CampusSnapshot,
  getFacilityAsset,
} from '@/config/campusConfig'
import { FacilityTierImage } from './FacilityTierImage'
import { Hammer, ArrowRight, ChevronsUp, Sparkles, type LucideIcon } from 'lucide-react'

interface FacilityCardProps {
  visual: FacilityVisual
  snapshot: CampusSnapshot
  icon: LucideIcon
}

export function FacilityCard({ visual, snapshot, icon }: FacilityCardProps) {
  const navigate = useNavigate()
  const tier = visual.getTier(snapshot)
  const asset = getFacilityAsset(visual, tier)
  const status = visual.getStatus?.(snapshot) ?? null

  const notBuilt = tier < 0
  // The aspiration: what the next tier looks like (or the first tier if unbuilt).
  // Courts don't progress on a linear tier ladder (indoor is a separate build,
  // not an upgrade), so the tease would lie — skip it for courts.
  const showTease = visual.id !== 'court'
  const nextTier = !showTease
    ? undefined
    : notBuilt
      ? visual.tiers[0]
      : visual.tiers.find(t => t.tier > tier)
  const isMaxed = showTease && !notBuilt && !nextTier

  return (
    <div className="facility-card" style={notBuilt ? { opacity: 0.75 } : undefined}>
      <FacilityTierImage visual={visual} tier={tier} icon={icon} />

      <div className="facility-body">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span className="card-title" style={{ fontSize: '0.85rem' }}>{visual.name}</span>
          {!notBuilt && (
            <span className="meta-text">Tier {Math.max(tier, visual.tiers[0]?.tier ?? 0)}</span>
          )}
        </div>

        {!notBuilt && asset && (
          <div className="meta-text" style={{ marginTop: '0.1rem' }}>{asset.caption}</div>
        )}

        {/* The aspiration: tease the next upgrade (or the max-level badge) */}
        {nextTier ? (
          <div
            style={{
              marginTop: '0.35rem', display: 'flex', alignItems: 'center', gap: '0.3rem',
              fontSize: '0.68rem', color: 'var(--gold)',
            }}
          >
            <ChevronsUp size={11} />
            <span style={{ color: 'var(--text-secondary)' }}>
              {notBuilt ? 'Al construir: ' : 'Próximo: '}
            </span>
            {nextTier.caption}
          </div>
        ) : isMaxed ? (
          <div style={{ marginTop: '0.35rem', fontSize: '0.68rem', color: 'var(--gold)', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
            <Sparkles size={11} /> Nivel máximo
          </div>
        ) : null}

        <div className="facility-status">
          {status && (
            <span className={`pill pill-${status.tone === 'ok' ? 'success' : status.tone === 'warn' ? 'warning' : 'danger'}`}>
              {status.label}
            </span>
          )}
          <button
            onClick={() => navigate(visual.manageRoute)}
            className="icon-btn"
            style={{ marginLeft: 'auto', fontSize: '0.7rem', fontWeight: 600 }}
          >
            {notBuilt ? (<><Hammer size={12} /> Construir</>) : (<>Gestionar <ArrowRight size={12} /></>)}
          </button>
        </div>
      </div>
    </div>
  )
}
